import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonTable from '../../../../common/table/ComonTable';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import SearchBar from '../../../../common/formfields/SearchBar';
import DeleteModal from '../../../../common/commonmodal/DeleteModal';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';

const RoleList = () => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [pageInfo, setPageInfo] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalPages: 1,
        totalRecords: 0
    });

    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);

    const fetchRoles = async () => {
        setLoading(true);
        try {
            const response = await apiServices({
                method: "GET",
                url: `/RoleMaster/GetAllRoles`
            });

            if (response.isSuccess || response.success || response.IsSuccess) {
                let apiData = response.data;
                let data = [];
                if (Array.isArray(apiData)) {
                    data = apiData;
                } else if (apiData && Array.isArray(apiData.data)) {
                    data = apiData.data;
                } else if (apiData && Array.isArray(apiData.RoleMasterData)) {
                    data = apiData.RoleMasterData;
                }

                setRoles(data);
                setPageInfo(prev => ({
                    ...prev,
                    totalRecords: data.length,
                    totalPages: Math.ceil(data.length / prev.pageSize)
                }));
            }
        } catch (error) {
            console.error("Error fetching roles:", error);
            toast.error("Failed to fetch roles.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRoles();
    }, []);

    const handleLimitChange = (newLimit) => {
        setPageInfo(prev => ({ ...prev, pageSize: newLimit, pageNumber: 1 }));
        setCurrentPage(1);
    };

    const tableHeaders = [
        { label: "NO", key: "no" },
        { label: "NAME", key: "roleName" },
        { label: "STATUS", key: "isActive" },
        { label: "ACTIONS", key: "actions" }
    ];

    const renderCell = (key, row, index) => {
        const val = (k) => row[k] || row[k.charAt(0).toLowerCase() + k.slice(1)] || "—";
        switch (key) {
            case "no":
                return <span className="text-xs font-semibold text-gray-800 dark:text-textprimary">{index + 1 + (currentPage - 1) * pageInfo.pageSize}</span>;
            case "roleName":
                return <span className="text-xs font-semibold text-gray-800 dark:text-textprimary">{row?.RoleName}</span>;
            case "isActive":
                const status = row.isActive || row.IsActive || 'Active';
                let statusColor = "bg-gray-100 text-gray-600";
                if (status === 'Active') statusColor = "bg-[#DCFCE7] text-[#166534]";
                if (status === 'Inactive') statusColor = "bg-[#FEF9C3] text-[#D97706]";
                return (
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black shadow-sm uppercase ${statusColor}`}>
                        {status}
                    </span>
                );
            case "actions":
                return null;
            default:
                return <span className="text-xs font-normal text-[#64748B] dark:text-textsecondary">{row[key] || "—"}</span>;
        }
    };

    const handleDeleteClick = (row) => {
        setRecordToDelete(row);
        setIsDeleteOpen(true);
    };

    const confirmDelete = async () => {
        if (!recordToDelete) return;
        try {
            const response = await apiServices({
                method: "DELETE",
                url: `/RoleMaster/RemoveExistingRole/${recordToDelete.RoleId || recordToDelete.roleId || recordToDelete.Id || recordToDelete.id}`
            });
            if (response.success || response.IsSuccess || response.isSuccess) {
                toast.success("Role deleted successfully.");
                fetchRoles();
            } else {
                toast.error(response.message || "Failed to delete role.");
            }
        } catch (error) {
            console.error("Delete error:", error);
            toast.error("An error occurred during deletion.");
        } finally {
            setIsDeleteOpen(false);
            setRecordToDelete(null);
        }
    };

    const filteredData = useMemo(() => {
        let data = roles;
        if (statusFilter) {
            data = data.filter(item => (item.isActive || item.IsActive || 'Active') === statusFilter);
        }
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            data = data.filter(item =>
                (item.roleName || item.RoleName || "").toLowerCase().includes(query)
            );
        }
        return data;
    }, [roles, statusFilter, searchQuery]);

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * pageInfo.pageSize;
        return filteredData.slice(start, start + pageInfo.pageSize);
    }, [filteredData, currentPage, pageInfo.pageSize]);

    const headerAction = (
        <div className="flex flex-col sm:flex-row items-center gap-3">

            <select
                value={statusFilter}
                onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                }}
                className="w-full sm:w-auto px-3 py-2 border border-gray-300 dark:border-bordercolor rounded-lg text-xs focus:ring-indigo-500 focus:border-indigo-500 text-gray-700 dark:text-textprimary bg-white dark:bg-actionitembg transition-colors"
            >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
            </select>
        </div>
    );

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto gap-6 pt-4">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading="Role Management"
                subHeading="Manage user roles and permissions"
                primaryBtnText="Add New Role"
                primaryBtnIcon={faPlus}
                PrimaryOnClick={() => navigate('/zentcomm/site-management/role/form')}
            />

            <div className="shrink-0 flex-1 flex flex-col">
                <CommonTable
                    tableHeading="Roles List"
                    headerAction={headerAction}
                    headers={tableHeaders}
                    data={paginatedData}
                    renderCell={(key, row) => renderCell(key, row, paginatedData.indexOf(row))}
                    pagination={{
                        ...pageInfo,
                        pageNumber: currentPage,
                        totalRecords: filteredData.length,
                        totalPages: Math.ceil(filteredData.length / pageInfo.pageSize) || 1
                    }}
                    onPageChange={setCurrentPage}
                    onLimitChange={handleLimitChange}
                    loading={loading}
                    showActionCol={true}
                    onEdit={(row) => navigate(`/zentcomm/site-management/role/form`, { state: { id: row.RoleId || row.roleId || row.Id || row.id } })}
                    onDelete={handleDeleteClick}
                />
            </div>

            <DeleteModal
                isOpen={isDeleteOpen}
                onClose={() => {
                    setIsDeleteOpen(false);
                    setRecordToDelete(null);
                }}
                onConfirm={confirmDelete}
                message={`Are you sure you want to delete the role "${recordToDelete?.roleName || recordToDelete?.RoleName}"?`}
            />
        </div>
    );
};

export default RoleList;
